// DDoS Protection — realigned to the v2 design pattern used by
// /v2/services/bot/config (see components/pages/_new/services/bot/Config.js):
//   * V2PageHeader for the page title / description / actions
//   * SectionCard primitive for each grouped surface
//   * MUI @mui/icons-material/*Outlined throughout
//   * Shared `colors` palette + INDIGO / BORDER / FG* locals
//
// The four protection metrics use WAF overview cards (full-width strip).
// Protection status (master switch) sits in its own SectionCard below.
// The 7 threshold panels are the body of a "Detection thresholds"
// "Detection thresholds" SectionCard.  The right-rail "Recent DDoS
// activity" panel mirrors BM Config's Live Impact Rail — a 30-second
// poll against /api/user/v1/config/ddos/:site_uid/observability.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Stack, Typography, Box, CircularProgress, Chip, Select, MenuItem } from "@mui/material";

import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// 2026-10-04 strip: six icons retired with their dead sections.
// See DDoSSettingsDrawer.js for the same list.
import WavesOutlinedIcon from "@mui/icons-material/WavesOutlined";

import { ReactComponent as EditIcon } from "../../../../../vendor/_new/icon/sites/edit.svg";

import { useNotification } from "../../../../../contexts/NotificationContext";
import PermissionGate from "../../../../guards/PermissionGate";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import { PrimaryButton, GhostButton } from "../../../../common/Button";
import { GreenSwitch } from "../../../../common/Greens";
import StickySaveBar from "../../../../common/StickySaveBar";
import { enabledLabel, fieldChange } from "../../../../common/stickySaveBarUtils";
import colors from "../../../../../utils/colors";
import {
  UserType,
  ConfigAction,
  DdosSensitivity,
  RateLimitMitigationTimeout,
} from "../../../../../utils/constants";

import useAuth from "../../../../../hooks/useAuth";
import useDdosConfig from "../../../../../hooks/user/useDdosConfig";
import useSite from "../../../../../hooks/user/useSite";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import usePermissions from "../../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../../utils/permissions";
import { DDoSSettingsDrawer, SENSITIVITY_LABELS, TIMEOUT_LABELS } from "./DDoSSettingsDrawer";
import DDoSStageControl from "./DDoSStageControl";
import DDoSRouteConfigsTable from "./DDoSRouteConfigsTable";
import WAFOverviewMetricCard, {
  WAFOverviewIconChip,
  WAFOverviewStatNumber,
  wafOverviewGridSxForCount,
  wafOverviewSubSx,
} from "../web_application/components/WAFOverviewMetricCard";

// Palette / tokens — kept in sync with BM Config's locals so the two
// service-config pages read as one visual family.
const INDIGO = colors.primary[300];
const INDIGO_BG = "rgba(99, 113, 247, 0.08)";
const INDIGO_RING = "rgba(99, 113, 247, 0.22)";
const BORDER = colors.neutral[200];
const BORDER_STRONG = colors.neutral[500];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];
const FG3 = colors.neutral[500];
const SUCCESS_BG = "#E8FBEF";
const SUCCESS_FG = "#0f7a42";
const SUCCESS_BORDER = "#C8E6D4";

// Every entry's `key` is the exact AD_SITE_OVERRIDE column on
// prisma.ddosConfig — server/constants/config/AD.js is the single
// source of truth for what the CP will accept.
// 2026-10-04 strip: see DDoSSettingsDrawer.js. Only Entropy/variance
// fields reach the Sentinel-X detector today; everything else stayed
// stored-but-dead and has been removed from the UI. The DB columns
// are untouched for rollback.
const THR_GROUPS = [
  {
    cat: "Entropy / variance",
    Icon: WavesOutlinedIcon,
    iconBg: SUCCESS_BG,
    iconColor: SUCCESS_FG,
    fields: [
      { key: "slidingWindow", label: "Sliding window", unit: "s", def: "30" },
      { key: "ddosVarianceThreshold", label: "Variance threshold", unit: "σ", def: "0.05" },
    ],
  },
];

const TOTAL_THR_FIELDS = THR_GROUPS.reduce((n, g) => n + g.fields.length, 0);

function fmtNumber(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString();
}

// ── Response-section helpers.
//
// The Response SectionCard groups the four behaviours an operator
// tunes together: detection aggressiveness, enforcement stage,
// challenge window, and browser-integrity. Shared primitives below
// so each row renders as "label + description on the left, control
// on the right" with hairline dividers between rows.

// One row in the Response grid. `isFirst` suppresses the top
// divider; everything else uses the shared grid-row separator
// rendered as a sibling.
function ResponseRow({ label, hint, control }) {
  return (
    <>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "inherit", lineHeight: "20px" }}>
          {label}
        </Typography>
        {hint && (
          <Typography
            sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: "18px", mt: 0.25 }}
          >
            {hint}
          </Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>{control}</Box>
    </>
  );
}

// Sensitivity pills — compact 3-way segmented in the Response grid.
// Writes land immediately via setDdosSensitivity; the pill rolls
// back on failure so the UI never diverges from the server view.
function SensitivitySegmented({ value, onChange, disabled }) {
  const opts = [
    { value: DdosSensitivity.LOW, label: "Low" },
    { value: DdosSensitivity.MEIDUM, label: "Medium" },
    { value: DdosSensitivity.HIGH, label: "High" },
  ];
  return (
    <Box
      role="radiogroup"
      aria-label="Sensitivity"
      sx={{
        display: "inline-grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: "4px",
        padding: "4px",
        background: colors.neutral[100],
        borderRadius: "8px",
        maxWidth: 420,
        width: "100%",
        opacity: disabled ? 0.55 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {opts.map((o) => {
        const on = o.value === value;
        return (
          <Box
            key={o.value}
            role="radio"
            aria-checked={on}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && onChange(o.value)}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onChange(o.value);
              }
            }}
            sx={{
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 500,
              borderRadius: "6px",
              cursor: disabled ? "not-allowed" : "pointer",
              color: on ? INDIGO : FG2,
              background: on ? "#fff" : "transparent",
              boxShadow: on ? "0 1px 2px rgba(17,17,17,0.06)" : "none",
              transition: "all 150ms",
              userSelect: "none",
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
}

// Mitigation timeout — single raw select mirroring the design row.
const TIMEOUT_ORDER = [
  RateLimitMitigationTimeout.THIRTY_SECONDS,
  RateLimitMitigationTimeout.ONE_MINUTE,
  RateLimitMitigationTimeout.TEN_MINUTES,
  RateLimitMitigationTimeout.ONE_HOUR,
  RateLimitMitigationTimeout.ONE_DAY,
];
function TimeoutPicker({ value, onChange, disabled }) {
  return (
    <Select
      value={value ?? ""}
      size="small"
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      disabled={disabled}
      sx={{
        minWidth: 180,
        fontSize: 13,
        background: "#fff",
        "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
      }}
    >
      {TIMEOUT_ORDER.map((t) => (
        <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
          {TIMEOUT_LABELS[t]}
        </MenuItem>
      ))}
    </Select>
  );
}

// ── SectionCard: mirrors BM Config's primitive so both service-config
// pages share the same header/body ergonomics.
function SectionCard({ icon, title, desc, right, children, bodyPad = "18px 20px" }) {
  const hasBody = children !== undefined;
  const header = (title || desc || icon || right) ? (
    <Box
      sx={{
        padding: "18px 20px 14px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.75,
        ...(hasBody ? { borderBottom: `1px solid ${BORDER}` } : {}),
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "9px",
              background: INDIGO_BG,
              color: INDIGO,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              "& svg": { width: 18, height: 18 },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          {title && (
            <Typography sx={{ fontWeight: 600, fontSize: 16, color: FG1, mb: 0.25 }}>
              {title}
            </Typography>
          )}
          {desc && (
            <Typography
              sx={{ fontSize: 12.5, color: FG2, lineHeight: 1.5, maxWidth: 540 }}
            >
              {desc}
            </Typography>
          )}
        </Box>
      </Box>
      {right}
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      {header}
      {children !== undefined && <Box sx={{ padding: bodyPad }}>{children}</Box>}
    </Box>
  );
}

// One threshold panel inside the Detection-thresholds SectionCard body.
// Read-only display of `label / value + unit`, with a faint `custom`
// pill when any field in the group carries an override.
function ThresholdPanel({ group, currentValues }) {
  const hasOvr = group.fields.some((f) => currentValues[f.key] != null);
  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.25}
        sx={{
          padding: "12px 16px",
          borderBottom: `1px solid ${BORDER}`,
          background: colors.neutral[50],
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              background: group.iconBg,
              color: group.iconColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              "& svg": { width: 16, height: 16 },
            }}
          >
            <group.Icon />
          </Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: FG1 }}>
            {group.cat}
          </Typography>
        </Stack>
        {hasOvr && (
          <Chip
            label="custom"
            size="small"
            sx={{
              fontSize: 10.5,
              height: 20,
              fontWeight: 600,
              background: INDIGO_BG,
              color: INDIGO,
              border: `1px solid ${INDIGO_RING}`,
            }}
          />
        )}
      </Stack>
      <Box sx={{ padding: "4px 16px 8px" }}>
        {group.fields.map((f) => {
          const custom = currentValues[f.key];
          const isDefault = custom == null;
          const displayVal = isDefault ? f.def : String(custom);
          const tightUnit = f.unit === "×" || f.unit === "σ";
          return (
            <Stack
              key={f.key}
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              spacing={1.5}
              sx={{
                padding: "10px 0",
                borderBottom: `1px solid ${colors.neutral[100]}`,
                "&:last-child": { borderBottom: 0 },
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: FG2, flex: 1, minWidth: 0, pr: 1.25 }}>
                {f.label}
              </Typography>
              <Box
                sx={{
                  fontSize: 13,
                  fontWeight: isDefault ? 500 : 600,
                  fontFamily: "monospace",
                  fontVariantNumeric: "tabular-nums",
                  color: isDefault ? colors.neutral[400] : FG1,
                  whiteSpace: "nowrap",
                  textAlign: "right",
                }}
              >
                {displayVal}
                {f.unit && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 11,
                      color: isDefault ? colors.neutral[400] : FG3,
                      fontWeight: 400,
                      ml: "2px",
                    }}
                  >
                    {tightUnit ? f.unit : ` ${f.unit}`}
                  </Box>
                )}
              </Box>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
}

// Right-rail live panel — mirrors BM Config's LiveImpactRail.  Polls
// /api/user/v1/config/ddos/:site_uid/observability every 30s via
// getDdosObservability (context-backed).
// 24-bar hourly sparkline (Gap 4).  Height-normalised to the highest
// bucket in the window so a quiet day still shows the pattern.  Zero
// buckets render as a 2px hairline so the operator can tell "no data"
// from "gap in the chart".
function TrendBars({ trend }) {
  const buckets = trend?.buckets || [];
  const max = Math.max(1, ...buckets.map((b) => b.blocks || 0));
  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="flex-end" spacing="2px" sx={{ height: 42 }}>
        {buckets.map((b) => {
          const h = b.blocks > 0 ? Math.max(2, Math.round((b.blocks / max) * 42)) : 2;
          const isRecent = b.blocks > 0;
          return (
            <Box
              key={b.hour_ts}
              title={`${new Date(b.hour_ts * 1000).toLocaleString()}\n${b.blocks.toLocaleString()} blocks · ${b.unique_asns} ASNs`}
              sx={{
                flex: 1,
                height: `${h}px`,
                background: isRecent ? INDIGO : colors.neutral[200],
                borderRadius: "1px",
                transition: "height 200ms",
              }}
            />
          );
        })}
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography sx={{ fontSize: 10, color: FG3 }}>24 h ago</Typography>
        <Typography sx={{ fontSize: 10, color: FG3 }}>now</Typography>
      </Stack>
    </Box>
  );
}

// Small chip mirroring BM Config's "streaming" chip. Colour comes from
// the AD-Engine health proxy — green when CH is receiving blocks, amber
// when quiet, grey when unknown.
function AdHealthChip({ health }) {
  const v = health?.verdict || "unknown";
  const map = {
    healthy: { label: "AD engine healthy",   bg: SUCCESS_BG,       fg: SUCCESS_FG, br: SUCCESS_BORDER },
    quiet:   { label: "AD engine quiet",     bg: "#FFF6D0",        fg: "#7a5b10", br: "#F2DE9A" },
    unknown: { label: "AD engine unknown",   bg: colors.neutral[100], fg: FG2,     br: BORDER },
  };
  const p = map[v] || map.unknown;
  return (
    <Chip
      label={p.label}
      size="small"
      sx={{
        fontSize: 11,
        height: 22,
        fontWeight: 600,
        background: p.bg,
        color: p.fg,
        border: `1px solid ${p.br}`,
        borderRadius: "999px",
      }}
    />
  );
}

function RecentActivityRail({ observability, trend, loading }) {
  const blocks24h = observability?.blocks_24h ?? null;
  const uniqueIps = observability?.unique_ips_24h ?? null;
  const activeNow = observability?.active_now ?? null;
  const top = observability?.top_offender ?? null;

  const empty = !loading && blocks24h === 0;

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          background: "#fff",
          border: `1px solid ${BORDER}`,
          borderRadius: "14px",
          padding: "16px 18px",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                color: FG2,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontWeight: 600,
              }}
            >
              Live · last 24 h
            </Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: FG1, mt: 0.5 }}>
              Recent DDoS activity
            </Typography>
          </Box>
          <Chip
            label="streaming"
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              background: SUCCESS_BG,
              color: SUCCESS_FG,
              border: `1px solid ${SUCCESS_BORDER}`,
              borderRadius: "999px",
            }}
          />
        </Stack>

        {loading && observability == null ? (
          <Typography sx={{ fontSize: 11.5, color: FG3, py: 2, textAlign: "center" }}>
            Loading…
          </Typography>
        ) : empty ? (
          <Typography sx={{ fontSize: 11.5, color: FG3, py: 2, textAlign: "center" }}>
            No DDoS blocks in the last 24 hours.
          </Typography>
        ) : (
          <>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: FG1, lineHeight: 1 }}>
                  {fmtNumber(blocks24h)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: FG3, mt: 0.5 }}>
                  blocks
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: FG1, lineHeight: 1 }}>
                  {fmtNumber(uniqueIps)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: FG3, mt: 0.5 }}>
                  unique IPs
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: FG1, lineHeight: 1 }}>
                  {activeNow != null ? fmtNumber(activeNow) : "—"}
                </Typography>
                <Typography sx={{ fontSize: 11, color: FG3, mt: 0.5 }}>
                  active now
                </Typography>
              </Box>
            </Stack>

            {trend?.buckets?.length ? <TrendBars trend={trend} /> : null}

            {top && (
              <Box
                sx={{
                  mt: 2,
                  padding: "10px 12px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "10px",
                  background: colors.neutral[50],
                }}
              >
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: FG3, letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.5 }}>
                  Top offender
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: FG1, fontFamily: "monospace" }}>
                  {top.ip}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: FG2, mt: 0.25 }}>
                  {fmtNumber(top.blocks)} blocks
                  {top.asn ? ` · ${top.asn}` : ""}
                  {top.country ? ` · ${top.country}` : ""}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}

// The design's "master protection" toggle needs a boolean column on
// ddos_configs that today does not exist — the model exposes only
// sensitivity (1–3) + timeout + browserIntegrity.  So the strip's
// Protection cell renders the current *effective* state (config row
// exists = active) and defers the kill-switch wiring.
const NewServicesDDosMigrationConfig = () => {
  const {
    isAuthenticated,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
    userType,
  } = useAuth();
  const { hasPermission } = usePermissions();
  const {
    ddosConfig,
    adConfig,
    detectorStage,
    ddosObservability,
    ddosTrend,
    adEngineHealth,
    getDdosConfig,
    getAdConfig,
    getDdosObservability,
    getDdosTrend,
    getAdEngineHealth,
    setDdosEnabled,
    setDdosSensitivity,
    setDdosMitigationTimeout,
    checkBrowserIntegrity,
    setErr,
    errMsg,
  } = useDdosConfig();
  const { showSuccess } = useNotification();
  const { getSitesForItems, siteList, settingApply } = useSite();
  const { showError } = useNotification();

  const canConfigureDDoS = hasPermission(PERMISSIONS.DDOS_CONFIGURE);
  const isReadonly = userType === UserType.ORG_USER || !canConfigureDDoS;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [enabledDraft, setEnabledDraft] = useState(true);
  const [savedEnabled, setSavedEnabled] = useState(true);
  const [isApplyingEnabled, setIsApplyingEnabled] = useState(false);

  useEffect(() => {
    if (isAuthenticated) getSitesForItems();
  }, [isAuthenticated, getSitesForItems]);

  const { effectiveSiteUid } = useGlobalSite();
  const selectedSiteId = effectiveSiteUid ?? "";

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (websiteController) websiteController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
    }
  }, [
    isAuthenticated,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
  ]);

  useEffect(() => {
    if (errMsg) {
      showError(errMsg);
      setErr(null);
    }
  }, [errMsg, showError, setErr]);

  const loadConfig = useCallback(() => {
    if (!selectedSiteId || !isAuthenticated) return;
    setConfigLoading(true);
    setConfigLoaded(false);
    Promise.all([getDdosConfig(selectedSiteId), getAdConfig(selectedSiteId)])
      .catch(() => {})
      .finally(() => {
        setConfigLoading(false);
        setConfigLoaded(true);
      });
  }, [selectedSiteId, isAuthenticated, getDdosConfig, getAdConfig]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Observability + trend + AD health all poll on the same 30s cadence.
  // Fresh values slide into the strip + rail without a page-level
  // loading state.
  useEffect(() => {
    if (!selectedSiteId || !isAuthenticated) return;
    const tick = () => {
      getDdosObservability(selectedSiteId);
      getDdosTrend(selectedSiteId);
      getAdEngineHealth();
    };
    tick();
    const h = setInterval(tick, 30_000);
    return () => clearInterval(h);
  }, [selectedSiteId, isAuthenticated, getDdosObservability, getDdosTrend, getAdEngineHealth]);

  const handleDrawerSaved = () => {
    loadConfig();
    if (selectedSiteId) {
      getDdosObservability(selectedSiteId);
      getDdosTrend(selectedSiteId);
    }
  };

  useEffect(() => {
    if (ddosConfig) {
      const enabled = ddosConfig.enabled !== false;
      setEnabledDraft(enabled);
      setSavedEnabled(enabled);
    }
  }, [ddosConfig]);

  const stickyModifiedFields = useMemo(() => {
    if (!ddosConfig || enabledDraft === savedEnabled) return [];
    return [fieldChange("DDoS protection", enabledLabel(savedEnabled), enabledLabel(enabledDraft))];
  }, [ddosConfig, enabledDraft, savedEnabled]);

  const stickyModifiedCount = stickyModifiedFields.length;

  const discardEnabledChange = useCallback(() => {
    setEnabledDraft(savedEnabled);
  }, [savedEnabled]);

  const applyEnabledChange = useCallback(async () => {
    if (isReadonly || !selectedSiteId || !ddosConfig || stickyModifiedCount === 0) return;
    setIsApplyingEnabled(true);
    try {
      const ok = await setDdosEnabled(selectedSiteId, enabledDraft);
      if (!ok) return;

      const site = siteList?.find((s) => s.id === selectedSiteId);
      if (site) {
        const result = await settingApply(site.site_id, ConfigAction.DDOS);
        if (result && result.status !== "success") {
          showError(result.msg || "Failed to apply configuration");
          return;
        }
      }

      setSavedEnabled(enabledDraft);
      showSuccess(enabledDraft ? "DDoS protection enabled" : "DDoS protection disabled");
    } finally {
      setIsApplyingEnabled(false);
    }
  }, [
    isReadonly,
    selectedSiteId,
    ddosConfig,
    stickyModifiedCount,
    enabledDraft,
    setDdosEnabled,
    siteList,
    settingApply,
    showError,
    showSuccess,
  ]);

  const stageEnabledToggle = () => {
    if (isReadonly || !ddosConfig) return;
    setEnabledDraft((v) => !v);
  };

  const overrideCount = useMemo(() => {
    if (!adConfig) return 0;
    return THR_GROUPS.flatMap((g) => g.fields).filter((f) => adConfig[f.key] != null).length;
  }, [adConfig]);

  const overrideValues = useMemo(() => {
    const out = {};
    if (!adConfig) return out;
    THR_GROUPS.flatMap((g) => g.fields).forEach((f) => {
      if (adConfig[f.key] != null) out[f.key] = adConfig[f.key];
    });
    return out;
  }, [adConfig]);

  const sensitivityLabel = ddosConfig
    ? SENSITIVITY_LABELS[ddosConfig.sensitivity] || `Level ${ddosConfig.sensitivity}`
    : "—";
  const timeoutLabel = ddosConfig
    ? TIMEOUT_LABELS[ddosConfig.timeout] || `${ddosConfig.timeout}s`
    : "—";
  const browserOn = !!ddosConfig?.browser_integrity;
  const blocks24h = ddosObservability?.blocks_24h ?? null;
  const activeNow = ddosObservability?.active_now ?? null;
  const scopedHost = ddosObservability?.host || null;
  // `enabled` is the operator kill-switch (Gap 1). `isProtected` also
  // requires a real config row so a brand-new site with no ddos_configs
  // entry doesn't render as active.
  const killSwitchEnabled = enabledDraft;
  const isProtected = ddosConfig != null && killSwitchEnabled;

  const editBtn = !isReadonly && selectedSiteId ? (
    <PrimaryButton
      startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
      onClick={() => setDrawerOpen(true)}
    >
      Edit configuration
    </PrimaryButton>
  ) : null;

  return (
    <PermissionGate permission={PERMISSIONS.DDOS_READ} resourceLabel="DDoS configuration">
      <>
        <Helmet title="DDoS Protection" />
        <Stack spacing={3} sx={{ p: 6 }}>
          <V2PageHeader
            title="DDoS Protection"
            titleSx={{ color: FG1 }}
            sx={{ pb: 0, alignItems: "center" }}
            description={
              <>
                Automatic detection and mitigation of volumetric and application-layer attacks
                {scopedHost ? (
                  <>
                    {" "}for <strong>{scopedHost}</strong>
                  </>
                ) : null}
                . Review the current configuration below, then edit sensitivity and per-vector
                thresholds.
              </>
            }
            helpTooltip="Monitor and configure DDoS mitigation for the selected site. Adjust sensitivity, challenge timeouts, and browser integrity checks against volumetric and application-layer attacks."
            actions={editBtn}
          />

          <SiteScopeGate mode="single">
            {() => (
              <>
                {configLoading && (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 240 }}>
                    <CircularProgress size={36} sx={{ color: INDIGO }} />
                    <Typography sx={{ fontSize: 13, color: FG2, mt: 2 }}>
                      Loading DDoS configuration…
                    </Typography>
                  </Stack>
                )}

                {configLoaded && !configLoading && (
                  <Stack spacing={3}>
                    <Box sx={wafOverviewGridSxForCount(4)}>
                      <WAFOverviewMetricCard
                        icon={(
                          <WAFOverviewIconChip variant="primary">
                            <SpeedOutlinedIcon sx={{ fontSize: 16 }} />
                          </WAFOverviewIconChip>
                        )}
                        label="Sensitivity"
                      >
                        <WAFOverviewStatNumber value={sensitivityLabel} />
                        <Typography sx={wafOverviewSubSx}>attack-detection profile</Typography>
                      </WAFOverviewMetricCard>
                      <WAFOverviewMetricCard
                        icon={(
                          <WAFOverviewIconChip variant="primary">
                            <TimerOutlinedIcon sx={{ fontSize: 16 }} />
                          </WAFOverviewIconChip>
                        )}
                        label="Mitigation timeout"
                      >
                        <WAFOverviewStatNumber value={timeoutLabel} />
                        <Typography sx={wafOverviewSubSx}>challenge window</Typography>
                      </WAFOverviewMetricCard>
                      <WAFOverviewMetricCard
                        icon={(
                          <WAFOverviewIconChip variant="primary">
                            <LockOutlinedIcon sx={{ fontSize: 16 }} />
                          </WAFOverviewIconChip>
                        )}
                        label="Browser check"
                      >
                        <WAFOverviewStatNumber value={browserOn ? "On" : "Off"} />
                        <Typography sx={wafOverviewSubSx}>integrity verification</Typography>
                      </WAFOverviewMetricCard>
                      <WAFOverviewMetricCard
                        highlight
                        icon={(
                          <WAFOverviewIconChip variant="primary">
                            <BlockOutlinedIcon sx={{ fontSize: 16 }} />
                          </WAFOverviewIconChip>
                        )}
                        label="Blocks (24 h)"
                      >
                        <WAFOverviewStatNumber value={fmtNumber(blocks24h)} />
                        <Typography sx={wafOverviewSubSx}>
                          {activeNow != null && activeNow > 0
                            ? `${activeNow} active mitigations`
                            : "no active mitigations"}
                        </Typography>
                      </WAFOverviewMetricCard>
                    </Box>

                    {/* Shadow-mode banner — surfaces the "we're
                        watching but not blocking" state so operators
                        don't miss it. The quick-action bumps
                        sensitivity to Medium, which (via Auto) also
                        promotes the enrollment stage to Canary. */}
                    {(() => {
                      const pinned = detectorStage?.stage || null;
                      const derived =
                        ddosConfig?.sensitivity === DdosSensitivity.LOW
                          ? "shadow"
                          : ddosConfig?.sensitivity === DdosSensitivity.MEIDUM
                          ? "canary"
                          : "authoritative";
                      const effective = pinned || derived;
                      if (effective !== "shadow" || !ddosConfig || isReadonly) return null;
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            padding: "14px 16px",
                            borderRadius: "8px",
                            background: "#FFF8E1",
                            border: "1px solid #FFE89A",
                            color: "#5e4610",
                          }}
                        >
                          <InfoOutlinedIcon sx={{ width: 18, height: 18, flexShrink: 0, mt: "1px" }} />
                          <Box sx={{ flex: 1, fontSize: 13, lineHeight: "20px" }}>
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              Running in shadow mode.
                            </Box>{" "}
                            Attacks are detected and logged, but no requests are blocked. Raise
                            sensitivity or set the enrollment stage to Canary to start enforcing.
                          </Box>
                          <Box
                            component="button"
                            type="button"
                            onClick={async () => {
                              const ok = await setDdosSensitivity(
                                selectedSiteId,
                                DdosSensitivity.MEIDUM
                              );
                              if (ok) await getDdosConfig(selectedSiteId);
                            }}
                            sx={{
                              flexShrink: 0,
                              height: 30,
                              padding: "0 12px",
                              borderRadius: "8px",
                              border: "1px solid #E8CF7A",
                              background: "#fff",
                              color: "#5e4610",
                              fontSize: 12.5,
                              fontWeight: 500,
                              cursor: "pointer",
                            }}
                          >
                            Switch to Medium
                          </Box>
                        </Box>
                      );
                    })()}

                    <SectionCard
                      icon={<GppGoodOutlinedIcon />}
                      title="Protection status"
                      desc="Master switch and top-level parameters. Tap Edit configuration to change any of these."
                      right={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={isProtected ? "Active" : "Disabled"}
                            size="small"
                            sx={{
                              fontSize: 11,
                              height: 22,
                              fontWeight: 600,
                              background: isProtected ? SUCCESS_BG : colors.neutral[100],
                              color: isProtected ? SUCCESS_FG : FG2,
                              border: `1px solid ${isProtected ? SUCCESS_BORDER : BORDER}`,
                            }}
                          />
                          <AdHealthChip health={adEngineHealth} />
                          {ddosConfig != null && !isReadonly && (
                            <GreenSwitch
                              checked={killSwitchEnabled}
                              onChange={stageEnabledToggle}
                              disabled={isApplyingEnabled}
                              inputProps={{
                                "aria-label": killSwitchEnabled
                                  ? "Disable DDoS protection"
                                  : "Enable DDoS protection",
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 380px" },
                        gap: 3,
                      }}
                    >
                      <Stack spacing={2}>
                        {/* Response — the primary inline-edit surface
                            for the four behaviours an operator tunes
                            together. Writes land per-field (no save
                            button): sensitivity/timeout/browser-check
                            each call their one-shot context setter,
                            and the Sentinel-X stage selector persists
                            through DDoSStageControl's own effect. */}
                        <SectionCard
                          title="Response"
                          desc="How aggressively attacks are detected, and what happens when one is."
                          bodyPad="20px"
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "minmax(0, 220px) minmax(0, 1fr)",
                              },
                              rowGap: 2.5,
                              columnGap: 4,
                              alignItems: "start",
                            }}
                          >
                            <ResponseRow
                              label="Sensitivity"
                              hint="Attack-detection profile. Also drives the enrollment stage when set to Auto."
                              control={
                                <SensitivitySegmented
                                  value={ddosConfig?.sensitivity ?? DdosSensitivity.HIGH}
                                  disabled={isReadonly || !selectedSiteId}
                                  onChange={async (v) => {
                                    const ok = await setDdosSensitivity(selectedSiteId, v);
                                    if (ok) await getDdosConfig(selectedSiteId);
                                  }}
                                />
                              }
                            />

                            <Box sx={{ gridColumn: "1 / -1", height: 1, background: BORDER }} />

                            <ResponseRow
                              label="Sentinel-X enrollment"
                              hint="Decides whether the engine's verdicts actually enforce."
                              control={
                                <DDoSStageControl
                                  siteUid={selectedSiteId}
                                  disabled={isReadonly}
                                />
                              }
                            />

                            <Box sx={{ gridColumn: "1 / -1", height: 1, background: BORDER }} />

                            <ResponseRow
                              label="Mitigation timeout"
                              hint="How long a challenge window stays open."
                              control={
                                <TimeoutPicker
                                  value={ddosConfig?.timeout}
                                  disabled={isReadonly || !selectedSiteId}
                                  onChange={async (v) => {
                                    const ok = await setDdosMitigationTimeout(selectedSiteId, v);
                                    if (ok) await getDdosConfig(selectedSiteId);
                                  }}
                                />
                              }
                            />

                            <Box sx={{ gridColumn: "1 / -1", height: 1, background: BORDER }} />

                            <ResponseRow
                              label="Browser check"
                              hint="Integrity verification for suspicious clients."
                              control={
                                <Stack direction="row" alignItems="center" spacing={1.25}>
                                  <GreenSwitch
                                    checked={browserOn}
                                    disabled={isReadonly || !selectedSiteId}
                                    onChange={async (e) => {
                                      const ok = await checkBrowserIntegrity(
                                        selectedSiteId,
                                        e.target.checked
                                      );
                                      if (ok) await getDdosConfig(selectedSiteId);
                                    }}
                                    inputProps={{ "aria-label": "Browser integrity check" }}
                                  />
                                  <Typography sx={{ fontSize: 13, color: FG2 }}>
                                    {browserOn ? "On" : "Off"}
                                  </Typography>
                                </Stack>
                              }
                            />
                          </Box>
                        </SectionCard>

                        {/* Per-route detection thresholds — the brain's
                            longest-match-with-priority resolver reads
                            this live. */}
                        <SectionCard
                          icon={<TuneOutlinedIcon />}
                          title="Per-route detection"
                          desc="Author path-pattern rules that override the global variance threshold for specific endpoints (e.g. /api/login strict, /static/* permissive)."
                        >
                          <DDoSRouteConfigsTable siteUid={selectedSiteId} />
                        </SectionCard>

                        <SectionCard
                          icon={<ShieldOutlinedIcon />}
                          title="Detection thresholds"
                          desc="Per-vector sensitivity floors. Defaults render in grey; any per-site override renders in indigo mono."
                          right={
                            <Chip
                              label={`${overrideCount} of ${TOTAL_THR_FIELDS} customised`}
                              size="small"
                              sx={{
                                fontSize: 11,
                                height: 22,
                                fontWeight: 500,
                                background: overrideCount > 0 ? INDIGO_BG : colors.neutral[100],
                                color: overrideCount > 0 ? INDIGO : FG2,
                                border: `1px solid ${
                                  overrideCount > 0 ? INDIGO_RING : BORDER
                                }`,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            />
                          }
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                              },
                              gap: 2,
                              alignItems: "start",
                            }}
                          >
                            {THR_GROUPS.map((g) => (
                              <ThresholdPanel
                                key={g.cat}
                                group={g}
                                currentValues={overrideValues}
                              />
                            ))}
                          </Box>
                          {!isReadonly && (
                            <GhostButton
                              fullWidth
                              startIcon={<EditIcon style={{ width: 15, height: 15 }} />}
                              onClick={() => setDrawerOpen(true)}
                              sx={{ mt: 2 }}
                            >
                              Edit thresholds
                            </GhostButton>
                          )}
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{ mt: 1.5, fontSize: 11.5, color: FG3 }}
                          >
                            <InfoOutlinedIcon sx={{ width: 14, height: 14 }} />
                            <span>Defaults come from the AD Engine's global config.</span>
                          </Stack>
                        </SectionCard>
                      </Stack>

                      <RecentActivityRail
                        observability={ddosObservability}
                        trend={ddosTrend}
                        loading={configLoading}
                      />
                    </Box>
                  </Stack>
                )}
              </>
            )}
          </SiteScopeGate>
        </Stack>

        <DDoSSettingsDrawer
          open={drawerOpen}
          handleClose={() => setDrawerOpen(false)}
          siteUid={selectedSiteId}
          onSaved={handleDrawerSaved}
        />

        <StickySaveBar
          open={!isReadonly && stickyModifiedCount > 0}
          modifiedCount={stickyModifiedCount}
          modifiedFields={stickyModifiedFields}
          onDiscard={discardEnabledChange}
          onApply={applyEnabledChange}
          isApplying={isApplyingEnabled}
        />
      </>
    </PermissionGate>
  );
};
export default NewServicesDDosMigrationConfig;
